// FeedPostUploader — modal estilo Instagram/Facebook para publicar al Feed.
//
// 3 pasos: 1) Selección de archivo, 2) Detalles + destino, 3) Confirmación.
// Usa MediaPicker como motor base. Se conecta a `mediaDestinations` para escribir
// según el rol del usuario.

import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Tag as TagIcon, Sparkles } from 'lucide-react';
import { MediaPicker } from './MediaPicker';
import { useAuth } from '@/contexts/AuthContext';
import { getDestinationsForRole, publishToDestination } from '@/utils/mediaDestinations';
import { postsApi } from '@/lib/postsApi';
import { toast } from 'sonner';
import { tr } from '@/contexts/LanguageContext';

const DEFAULT_CATEGORIES = [tr("Fade"), 'Beard', 'Transformation', 'Design', 'Freestyle', 'Classic Cut', 'Color', 'Styling'];

const DEFAULT_TAGS = [
  'fade', 'taper', 'skin-fade', 'mid-fade',
  'beard', 'lineup', 'edge-up',
  'transformation', 'before-after',
  'design', 'creative', 'freestyle',
  'competition', 'tournament', 'battle'
];

const VISIBILITY_OPTIONS = [
  { id: 'public',    label: tr("Público — todos pueden ver") },
  { id: 'followers', label: tr("Solo seguidores") },
  { id: 'private',   label: tr("Privado (solo tú)") }
];

export const FeedPostUploader = ({
  open,
  onOpenChange,
  preselectedDestination = null
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [media, setMedia] = useState(null);
  const [destination, setDestination] = useState(preselectedDestination || '');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [fields, setFields] = useState({
    title: '',
    description: '',
    category: 'Freestyle',
    tags: [],
    visibility: 'public',
    location: '',
    relatedBattle: '',
    relatedTournament: '',
    relatedChallenge: ''
  });

  const availableDestinations = useMemo(
    () => getDestinationsForRole(user?.role),
    [user?.role]
  );

  // Reset al abrir/cerrar
  useEffect(() => {
    if (open) {
      setStep(1);
      setMedia(null);
      setPublished(null);
      setPublishing(false);
      setDestination(preselectedDestination || availableDestinations[0]?.id || '');
      setFields({
        title: '',
        description: '',
        category: 'Freestyle',
        tags: [],
        visibility: 'public',
        location: '',
        relatedBattle: '',
        relatedTournament: '',
        relatedChallenge: ''
      });
    }
  }, [open, preselectedDestination, availableDestinations]);

  const set = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const toggleTag = (t) => setFields((p) => ({
    ...p,
    tags: p.tags.includes(t) ? p.tags.filter((x) => x !== t) : [...p.tags, t]
  }));

  const destObj = availableDestinations.find((d) => d.id === destination);

  const canGoStep2 = !!media;
  const canPublish = !!media && !!destination && (fields.description.trim().length > 0 || destObj?.id === 'client_avatar');

  const handlePublish = async () => {
    if (!destObj) return;
    setPublishing(true);
    try {
      // 1. Local persistence (legacy + offline-friendly).
      const entry = publishToDestination(destObj.id, { user, media, fields });

      // 2. Backend persistence — only for feed/social destinations. The other
      // destinations (course, product, ad) are handled by their own services.
      const FEED_KINDS = ['feed_post', 'reel', 'before_after', 'portfolio_photo',
                          'battle_content', 'tournament_content'];
      if (FEED_KINDS.includes(destObj.id)) {
        try {
          const kindMap = {
            feed_post: media?.kind === 'video' ? 'video' : 'photo',
            reel: 'reel',
            before_after: 'before_after',
            portfolio_photo: 'portfolio',
            battle_content: media?.kind === 'video' ? 'video' : 'photo',
            tournament_content: media?.kind === 'video' ? 'video' : 'photo',
          };
          const payload = {
            kind: kindMap[destObj.id] || 'photo',
            caption: fields.description || fields.title || '',
            tags: Array.isArray(fields.tags) ? fields.tags : [],
          };
          if (media?.kind === 'video') {
            payload.video_url = media.url || media.dataUrl || '';
            payload.thumbnail_url = media.poster || media.thumbnail || '';
          } else {
            payload.image_url = media?.url || media?.dataUrl || '';
          }
          if (destObj.id === 'before_after') {
            payload.before_url = media?.beforeUrl || media?.before || '';
            payload.after_url = media?.afterUrl || media?.after || media?.url || '';
          }
          await postsApi.create(payload);
        } catch (err) {
          // Non-fatal — local mode still works. The user gets a soft warning.
          console.warn('Backend post create failed', err);
        }
      }

      setPublished({ entry, dest: destObj });
      toast.success(destObj.requiresApproval
        ? tr("¡Enviado para revisión!")
        : tr("¡Publicado!"));
      setStep(3);
    } catch (e) {
      toast.error(e?.message || tr("Error al publicar"));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="feed-post-uploader">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {step === 1 && tr("Sube una foto o video")}
            {step === 2 && tr("Detalles del post")}
            {step === 3 && tr("¡Publicado!")}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && tr("Selecciona un archivo desde galería, archivos o cámara.")}
            {step === 2 && tr("Agrega descripción, categoría, tags y dónde se mostrará.")}
            {step === 3 && (destObj?.requiresApproval ? tr("Tu contenido está en revisión por el equipo.") : tr("Tu contenido ya está disponible."))}
          </DialogDescription>
        </DialogHeader>

        {/* PROGRESS DOTS */}
        <div className="flex items-center justify-center gap-1.5 my-2" data-testid="feed-uploader-stepper">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              data-testid={`feed-uploader-step-${n}`}
              className={`h-1.5 rounded-full transition-all ${
                n === step ? 'w-8 bg-primary' : n < step ? 'w-4 bg-primary/60' : 'w-4 bg-secondary'
              }`}
            />
          ))}
        </div>

        {/* ====== STEP 1: PICK FILE ====== */}
        {step === 1 && (
          <div className="space-y-4" data-testid="feed-uploader-step-1-pane">
            <MediaPicker
              accept={destObj?.mediaAccept || 'both'}
              value={media}
              onChange={setMedia}
              policyContext={destObj?.id || null}
              testIdPrefix="fpu-picker"
              placeholder={tr("Toca para abrir galería, archivos o cámara")}
            />

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="fpu-cancel">{tr("Cancelar")}</Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!canGoStep2}
                className="gap-1 bg-primary text-primary-foreground"
                data-testid="fpu-next-1"
              >{tr("Continuar")}{' '}<ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ====== STEP 2: DETAILS ====== */}
        {step === 2 && (
          <div className="space-y-4" data-testid="feed-uploader-step-2-pane">
            {/* Mini preview */}
            <div className="flex items-start gap-3 bg-background border border-border rounded-xl p-3">
              {media?.type === 'image' ? (
                <img src={media.dataUrl} alt="preview" className="w-20 h-20 rounded-lg object-cover bg-black" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-black flex items-center justify-center overflow-hidden">
                  {media?.thumbnailDataUrl
                    ? <img src={media.thumbnailDataUrl} alt="preview" className="w-full h-full object-cover" />
                    : <span className="text-[10px] text-white">VIDEO</span>}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{tr("Archivo seleccionado")}</p>
                <p className="text-sm font-semibold text-foreground truncate">{media?.name || '—'}</p>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-xs mt-1 px-0 h-auto" data-testid="fpu-back-1">
                  <ArrowLeft className="w-3 h-3 mr-1" />{' '}{tr("Cambiar archivo")}</Button>
              </div>
            </div>

            {/* Destination picker */}
            {availableDestinations.length > 1 && (
              <div>
                <Label className="text-xs">{tr("¿Dónde se publicará?")}</Label>
                <Select value={destination} onValueChange={setDestination}>
                  <SelectTrigger className="bg-background" data-testid="fpu-destination">
                    <SelectValue placeholder={tr("Selecciona destino")} />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {availableDestinations.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {destObj?.description && (
                  <p className="text-[11px] text-muted-foreground mt-1">{destObj.description}</p>
                )}
                {destObj?.requiresApproval && (
                  <Badge className="bg-amber-500/15 border-amber-500/40 text-amber-500 border text-[10px] mt-1">{tr("Requiere aprobación")}</Badge>
                )}
              </div>
            )}

            {/* Title (opcional) */}
            <div>
              <Label className="text-xs">{tr("Título (opcional)")}</Label>
              <Input
                value={fields.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder={tr("Ej. Skin fade clásico")}
                className="bg-background"
                data-testid="fpu-title"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs">{tr("Descripción {0}", destObj?.id !== 'client_avatar' && '*')}</Label>
              <Textarea
                value={fields.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder={tr("Comparte detalles del corte, técnica, productos…")}
                className="bg-background min-h-[80px]"
                data-testid="fpu-description"
              />
              <p className="text-[10px] text-muted-foreground mt-1">{fields.description.length}/500</p>
            </div>

            {/* Category */}
            {user?.role === 'barber' && (
              <div>
                <Label className="text-xs">{tr("Categoría")}</Label>
                <Select value={fields.category} onValueChange={(v) => set('category', v)}>
                  <SelectTrigger className="bg-background" data-testid="fpu-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {DEFAULT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Tags */}
            <div>
              <Label className="text-xs flex items-center gap-1"><TagIcon className="w-3 h-3" /> Tags</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {DEFAULT_TAGS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    data-testid={`fpu-tag-${t}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                      fields.tags.includes(t)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-muted-foreground border-border hover:border-primary/40'
                    }`}
                  >
                    #{t}
                  </button>
                ))}
              </div>
            </div>

            {/* Visibility */}
            <div>
              <Label className="text-xs">{tr("Visibilidad")}</Label>
              <Select value={fields.visibility} onValueChange={(v) => set('visibility', v)}>
                <SelectTrigger className="bg-background" data-testid="fpu-visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {VISIBILITY_OPTIONS.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div>
              <Label className="text-xs">{tr("Ubicación (opcional)")}</Label>
              <Input
                value={fields.location}
                onChange={(e) => set('location', e.target.value)}
                placeholder={tr("Miami, FL o nombre de la barbería")}
                className="bg-background"
                data-testid="fpu-location"
              />
            </div>

            <div className="flex justify-between gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => setStep(1)} className="gap-1" data-testid="fpu-back-2">
                <ArrowLeft className="w-4 h-4" />{' '}{tr("Atrás")}</Button>
              <Button
                onClick={handlePublish}
                disabled={!canPublish || publishing}
                className="gap-1 bg-primary text-primary-foreground"
                data-testid="fpu-publish"
              >
                {publishing
                  ? <><Loader2 className="w-4 h-4 animate-spin" />{' '}{tr("Publicando…")}</>
                  : <>{tr("Publicar")}{' '}<ArrowRight className="w-4 h-4" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* ====== STEP 3: SUCCESS ====== */}
        {step === 3 && published && (
          <div className="text-center py-6 space-y-3" data-testid="feed-uploader-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="font-display text-xl text-foreground">
              {published.dest.requiresApproval ? tr("Enviado para revisión") : tr("¡Publicado!")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {published.dest.requiresApproval
                ? tr("El equipo revisará tu contenido en las próximas horas. Recibirás una notificación cuando esté aprobado.")
                : tr("Tu contenido ya está disponible en \"{0}\".", published.dest.label)}
            </p>
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="fpu-success-close">{tr("Cerrar")}</Button>
              <Button
                onClick={() => {
                  setStep(1);
                  setMedia(null);
                  setPublished(null);
                }}
                className="bg-primary text-primary-foreground"
                data-testid="fpu-success-another"
              >{tr("Subir otro")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
